import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { isCalendarDate } from '../check-ins/schema';

// ─── member quit dates vocabulary ───────────────────────────────────────────
//
// Governed enum for the append-only `member_quit_dates` history — the
// Galapagos home for the tobacco quit date that Pelago 1.0 keeps in
// `users.quitDate` + `quit_dates` (ADR-128). A quit date is per programme
// (member_program), not per member: a member quitting tobacco while reducing
// alcohol has different targets. The current quit date is the latest row per
// member_program; a row with a null `quit_date` records that the date was
// cleared. New capture surfaces are added here (plus the DB CHECK) as their
// write paths land, never free-typed at a call site.

/**
 * Where a quit-date change originated. `mobile_app`: the member set it in the
 * app. `atlas_staff`: a care-team user set it on the member's behalf.
 * `system`: derived by a server process (for example a default or a plan
 * outcome). `backend2_import`: carried over from the Pelago 1.0 `quit_dates`
 * history during migration; `recorded_by` then holds the legacy creator kind.
 */
export const MEMBER_QUIT_DATE_SOURCE = [
  'mobile_app',
  'atlas_staff',
  'system',
  'backend2_import',
] as const;
export const memberQuitDateSourceSchema = z.enum(MEMBER_QUIT_DATE_SOURCE);
export type MemberQuitDateSource = z.infer<typeof memberQuitDateSourceSchema>;

/**
 * A quit date on the wire: a local calendar day (`YYYY-MM-DD`) in the member's
 * own zone (ADR-095), the day the member chose, never an instant. The `date`
 * column derives as a bare string; this is the narrowing every boundary uses.
 */
export const memberQuitDateLocalDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD local date')
  .refine(isCalendarDate, 'must be a real calendar date');

/**
 * Wire shape of one quit-date history row: a coerced projection of the table
 * (`source` narrowed to the vocabulary, `quitDate` to a local day), so it is
 * hand-authored and tied to the table by `quit-dates.drift.test.ts` (ADR-066
 * Rule 3). Null `quitDate` means the quit date was cleared by this row.
 */
export const memberQuitDateRecordSchema = z
  .object({
    id: z.string().uuid(),
    memberId: memberIdSchema,
    memberProgramId: z.string().uuid(),
    quitDate: memberQuitDateLocalDaySchema.nullable(),
    source: memberQuitDateSourceSchema,
    recordedBy: z.string().min(1),
    createdAt: z.string(),
  })
  .strict();
export type MemberQuitDateRecord = z.infer<typeof memberQuitDateRecordSchema>;

// ─── which programmes a quit date applies to ────────────────────────────────
//
// A server-owned key set, for the same reasons `NRT_ELIGIBLE_PROGRAM_KEYS` is
// one: the gate is legible in code, changing it is a reviewed diff, and a test
// cross-checks it against the programme catalogue so a renamed key fails
// loudly instead of quietly giving nobody a quit date.
//
// Cigarettes, vaping and smokeless tobacco are one `tobacco` programme; the
// product a member uses is only a tag on the episode. So the key set covers all
// three, and every combination with another programme, without any sub-product
// check.
//
// The literal is reachable from client bundles through the root barrel. ADR-015
// allows it because no client reads or branches on it: a client receives a
// quit date state whose `not_applicable` phase and `not_enrolled_in_programme`
// refusal never name the programme.

/** Programme keys whose members have a quit date. */
export const QUIT_DATE_PROGRAM_KEYS = Object.freeze(['tobacco'] as const);
export type QuitDateProgramKey = (typeof QUIT_DATE_PROGRAM_KEYS)[number];

/**
 * Does a quit date apply to this programme? FAILS CLOSED: anything that is not
 * exactly a key in the set (absent, blank, padded, differently cased, unknown)
 * is `false`, so an unresolvable enrolment never gets a quit date.
 */
export function isQuitDateProgramKey(
  programKey: string | null | undefined,
): programKey is QuitDateProgramKey {
  return QUIT_DATE_PROGRAM_KEYS.some((key) => key === programKey);
}

// ─── write refusal vocabulary ───────────────────────────────────────────────
//
// The `error.code` values a quit date write may return (ADR-002 Rule 4).
// `not_enrolled_in_programme` is deliberately the same string NRT returns for
// the same situation: the member has no active episode of a programme the
// feature applies to, said without naming the condition. `quit_date_read_only`
// answers a write against a state whose `canChange` is false, so a read-only
// state can ship from the server without a client learning a new rule. The
// read never refuses; it answers with the `not_applicable` phase instead.

export const QUIT_DATE_ERROR_CODES = {
  /** No active episode of a quit date programme to write the date to. */
  notEnrolledInProgramme: 'not_enrolled_in_programme',
  /** Not a real `YYYY-MM-DD` day, or outside the earliest and latest allowed dates. */
  invalidQuitDate: 'invalid_quit_date',
  /** The member's quit date state does not allow a change (`canChange` is false). */
  quitDateReadOnly: 'quit_date_read_only',
} as const;

export type QuitDateErrorCode = (typeof QUIT_DATE_ERROR_CODES)[keyof typeof QUIT_DATE_ERROR_CODES];
